import { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../state/AuthContext";
import { DEMO_EMPLOYEE } from "../lib/roles";
import { LOCAL_SEED, type RecordRow } from "../data/localSeed";
import { PageHeader, RequestRoutingPanel, type RoutingStep } from "../components/RequestRouting";
import { rankExpenseFaqs } from "../lib/expenseFaq";

type ExpenseRow = {
  id: string;
  employee: string;
  purpose: string;
  amount: number;
  status: string;
  submitted_at?: string;
  decided_at?: string;
  reject_reason?: string;
  notes?: string;
};

function expenseRoutingSteps(e: ExpenseRow): RoutingStep[] {
  const st = e.status;
  const pending = st === "Pending" || st.includes("Pending");
  const approved = st === "Approved";
  const rejected = st === "Rejected";
  return [
    {
      id: "1",
      title: "Submitted",
      role: "You",
      status: "completed",
      at: e.submitted_at,
      note: e.purpose,
    },
    {
      id: "2",
      title: "Supervisor review",
      role: "Jordan Ortiz",
      status: pending ? "current" : "completed",
      at: pending ? undefined : e.decided_at,
    },
    {
      id: "3",
      title: rejected ? "Rejected" : "Approved",
      role: "Jordan Ortiz",
      status: approved ? "completed" : rejected ? "rejected" : "upcoming",
      at: approved || rejected ? e.decided_at : undefined,
      note: rejected ? e.reject_reason : undefined,
    },
  ];
}

function seedExpense(row: RecordRow): ExpenseRow {
  const st = String(row.status || "");
  const base: ExpenseRow = {
    id: String(row.id),
    employee: String(row.employee || ""),
    purpose: String(row.purpose || ""),
    amount: Number(row.amount) || 0,
    status: st,
  };
  if (st !== "Draft") {
    base.submitted_at = String(row.submitted_at || "2026-07-20T15:00:00.000Z");
  }
  if (st === "Approved" || st === "Rejected") {
    base.decided_at = String(row.decided_at || "2026-07-22T12:00:00.000Z");
  }
  return base;
}

export function ExpensesPage() {
  const { moduleIds, homePath, roleIds } = useAuth();
  const canApprove =
    roleIds.includes("ROLE-SUPERVISOR") || roleIds.includes("ROLE-FINANCE-ACCOUNTING");
  const [purpose, setPurpose] = useState("");
  const [notes, setNotes] = useState("");
  const [amount, setAmount] = useState("");
  const [rows, setRows] = useState<ExpenseRow[]>(() =>
    (LOCAL_SEED.expenses ?? []).map(seedExpense),
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const mine = useMemo(
    () =>
      rows.filter(
        (e) =>
          String(e.employee).includes("Chen") || String(e.employee) === "A. Chen",
      ),
    [rows],
  );

  const queue = useMemo(
    () =>
      rows.filter(
        (e) =>
          String(e.status) === "Pending" || String(e.status).includes("Pending"),
      ),
    [rows],
  );

  const selected =
    rows.find((r) => String(r.id) === selectedId) ??
    mine.find((r) => String(r.id) === selectedId) ??
    mine[0] ??
    null;

  const faqs = useMemo(() => rankExpenseFaqs(`${purpose}\n${notes}`), [purpose, notes]);

  if (!moduleIds.includes("expenses")) return <Navigate to={homePath} replace />;

  function submitExpense(e: React.FormEvent) {
    e.preventDefault();
    const amt = Number(amount);
    if (!purpose.trim() || !Number.isFinite(amt)) return;
    const id = `ex-${Date.now()}`;
    const now = new Date().toISOString();
    const row: ExpenseRow = {
      id,
      employee: DEMO_EMPLOYEE.name,
      purpose: purpose.trim(),
      amount: amt,
      status: "Pending",
      submitted_at: now,
      notes: notes.trim() || undefined,
    };
    setRows((prev) => [row, ...prev]);
    setSelectedId(id);
    setPurpose("");
    setNotes("");
    setAmount("");
  }

  function approveExpense(id: string) {
    const now = new Date().toISOString();
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const next: ExpenseRow = {
          ...r,
          status: "Approved",
          decided_at: now,
        };
        delete next.reject_reason;
        return next;
      }),
    );
  }

  function rejectExpense(id: string) {
    const now = new Date().toISOString();
    setRows((prev) =>
      prev.map((r) =>
        r.id === id
          ? { ...r, status: "Rejected", decided_at: now, reject_reason: "Policy exception" }
          : r,
      ),
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Expenses & travel"
        subtitle="Submit expenses, track approval routing, and check policy guidance."
      />

      <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-4">
        <div className="space-y-4">
          <form className="bg-white border border-black/5 p-4 space-y-3" onSubmit={submitExpense}>
            <h2 className="font-semibold text-navy">New expense</h2>
            <label className="block text-sm font-medium text-navy">
              Purpose
              <input
                className="mt-1 w-full border border-black/10 px-3 py-2"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                placeholder="e.g. Client dinner, airfare, mileage"
                required
              />
            </label>
            <label className="block text-sm font-medium text-navy">
              Amount
              <input
                className="mt-1 w-full border border-black/10 px-3 py-2"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputMode="decimal"
                required
              />
            </label>
            <label className="block text-sm font-medium text-navy">
              Notes
              <textarea
                className="mt-1 w-full border border-black/10 px-3 py-2 min-h-[80px]"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Hotel taxes, alcohol on receipt, etc."
              />
            </label>
            <button type="submit" className="px-4 py-2 bg-navy text-white text-sm">
              Submit expense
            </button>
          </form>

          {canApprove && queue.length > 0 && (
            <div className="bg-white border border-black/5 p-3">
              <div className="text-xs uppercase tracking-wider text-slate mb-2">
                Approval queue
              </div>
              <ul className="space-y-2 text-sm">
                {queue.map((q) => (
                  <li
                    key={String(q.id)}
                    className="flex flex-wrap items-center justify-between gap-2 border border-black/5 px-3 py-2"
                  >
                    <button
                      type="button"
                      className="text-left hover:text-teal"
                      onClick={() => setSelectedId(String(q.id))}
                    >
                      {String(q.employee)} · {String(q.purpose)} ·{" "}
                      {Number(q.amount).toLocaleString(undefined, {
                        style: "currency",
                        currency: "USD",
                      })}
                    </button>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="text-xs px-2 py-1 bg-teal text-white"
                        onClick={() => approveExpense(String(q.id))}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        className="text-xs px-2 py-1 border border-red-500/40 text-red-700"
                        onClick={() => rejectExpense(String(q.id))}
                      >
                        Reject
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="bg-white border border-black/5 overflow-hidden">
              <div className="px-4 py-3 border-b border-black/5 font-semibold text-navy">
                Your expenses ({DEMO_EMPLOYEE.name})
              </div>
              <ul className="divide-y divide-black/5">
                {mine.map((e) => (
                  <li key={String(e.id)}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(String(e.id))}
                      className={`w-full text-left px-4 py-3 text-sm hover:bg-mist ${
                        selected && String(selected.id) === String(e.id)
                          ? "bg-mist border-l-2 border-l-teal"
                          : ""
                      }`}
                    >
                      <div className="font-medium text-navy">{String(e.purpose)}</div>
                      <div className="text-xs text-slate">
                        {Number(e.amount).toLocaleString(undefined, {
                          style: "currency",
                          currency: "USD",
                        })}{" "}
                        · {String(e.status)}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white border border-black/5 p-4">
              {selected ? (
                <RequestRoutingPanel
                  title="Expense routing"
                  steps={expenseRoutingSteps(selected)}
                />
              ) : (
                <p className="text-sm text-slate">Select an expense to see routing.</p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white border border-black/5 p-4 flex flex-col min-h-[280px]">
          <h2 className="font-semibold text-navy mb-2">Expense policy FAQ</h2>
          <p className="text-xs text-slate mb-3">Ranks from your purpose and notes.</p>
          <div className="space-y-2 overflow-auto flex-1">
            {faqs.slice(0, 5).map((f) => (
              <details key={f.id} className="border border-black/5 px-3 py-2">
                <summary className="text-sm font-medium text-navy cursor-pointer">
                  {f.title}
                </summary>
                <p className="text-sm text-slate mt-2">{f.body}</p>
              </details>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
